import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Mail, User, Building, MessageSquare, CheckCircle, AlertCircle } from 'lucide-react';
// Declare emailjs on window object
declare global {
  interface Window {
    emailjs?: any;
  }
}
export function Footer() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  // Load EmailJS script and initialize
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js';
    script.async = true;
    script.onload = () => {
      if (window.emailjs) {
        window.emailjs.init('QbBcY3oRJr0RXoo-P');
      }
    };
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  const scrollToTop = (e: React.MouseEvent) => {
    e.preventDefault();
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!window.emailjs) {
      setSubmitStatus('error');
      setStatusMessage('Email service not loaded. Please try again.');
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus('idle');
    // Format the content for the email template
    const emailContent = `
New Contact Form Submission

Name: ${formData.name}
Email: ${formData.email}
Company: ${formData.company || 'Not provided'}

Message:
${formData.message}
    `.trim();
    try {
      await window.emailjs.send('service_qevocv9', 'template_l1qoaog', {
        content: emailContent
      });
      setSubmitStatus('success');
      setStatusMessage("Message sent successfully! We'll get back to you within 24 hours.");
      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        message: ''
      });
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus('idle');
        setStatusMessage('');
      }, 5000);
    } catch (error) {
      console.error('EmailJS error:', error);
      setSubmitStatus('error');
      setStatusMessage('Failed to send message. Please try again or contact us directly.');
      // Clear error message after 5 seconds
      setTimeout(() => {
        setSubmitStatus('idle');
        setStatusMessage('');
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  return <footer id="contact" className="relative py-32 px-6 lg:px-8 bg-[#0A1628] overflow-hidden" data-id="element-40">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-[#0F52BA]/20 to-transparent rounded-full blur-3xl" data-id="element-41"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[#06B6D4]/10 to-transparent rounded-full blur-3xl" data-id="element-42"></div>

      <div className="max-w-7xl mx-auto relative z-10" data-id="element-43">
        <div className="grid lg:grid-cols-2 gap-16 items-start" data-id="element-44">
          {/* Left side - Content */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-45">
            <div className="inline-block px-4 py-2 bg-gradient-to-r from-[#0F52BA]/10 to-[#06B6D4]/10 rounded-full mb-6" data-id="element-46">
              <span className="text-sm font-semibold bg-gradient-to-r from-[#0F52BA] to-[#06B6D4] bg-clip-text text-transparent" data-id="element-47">
                Get in Touch
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight text-white" style={{
            fontFamily: 'Space Grotesk, sans-serif'
          }} data-id="element-48">
              Ready to turn your growth challenge into action?
            </h2>

            <p className="text-xl leading-relaxed text-gray-300 mb-8" data-id="element-49">
              Let's discuss how we can help you define bold bets, validate in
              the real world, and scale what works.
            </p>

            <div className="space-y-4" data-id="element-50">
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2
            }} className="flex items-center gap-3 text-gray-300" data-id="element-51">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center" data-id="element-52">
                  <Mail className="w-5 h-5 text-[#06B6D4]" data-id="element-53" />
                </div>
                <span data-id="element-54">We typically respond within 24 hours</span>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.3
            }} className="flex items-center gap-3 text-gray-300" data-id="element-55">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center" data-id="element-56">
                  <MessageSquare className="w-5 h-5 text-[#06B6D4]" data-id="element-57" />
                </div>
                <span data-id="element-58">All conversations are confidential</span>
              </motion.div>
            </div>
          </motion.div>

          {/* Right side - Form */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-59">
            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-60">
              {/* Name field */}
              <div data-id="element-61">
                <label htmlFor="name" className="block text-sm font-semibold text-white mb-2" data-id="element-62">
                  Name *
                </label>
                <div className="relative" data-id="element-63">
                  <User className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors ${focusedField === 'name' ? 'text-[#06B6D4]' : 'text-gray-500'}`} data-id="element-64" />
                  <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} onFocus={() => setFocusedField('name')} onBlur={() => setFocusedField(null)} disabled={isSubmitting} className="w-full pl-12 pr-4 py-4 bg-white/5 border-2 border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-[#06B6D4] focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed" placeholder="Your name" data-id="element-65" />
                </div>
              </div>

              {/* Email field */}
              <div data-id="element-66">
                <label htmlFor="email" className="block text-sm font-semibold text-white mb-2" data-id="element-67">
                  Email *
                </label>
                <div className="relative" data-id="element-68">
                  <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors ${focusedField === 'email' ? 'text-[#06B6D4]' : 'text-gray-500'}`} data-id="element-69" />
                  <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)} disabled={isSubmitting} className="w-full pl-12 pr-4 py-4 bg-white/5 border-2 border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-[#06B6D4] focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed" placeholder="your@email.com" data-id="element-70" />
                </div>
              </div>

              {/* Company field */}
              <div data-id="element-71">
                <label htmlFor="company" className="block text-sm font-semibold text-white mb-2" data-id="element-72">
                  Company
                </label>
                <div className="relative" data-id="element-73">
                  <Building className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors ${focusedField === 'company' ? 'text-[#06B6D4]' : 'text-gray-500'}`} data-id="element-74" />
                  <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} onFocus={() => setFocusedField('company')} onBlur={() => setFocusedField(null)} disabled={isSubmitting} className="w-full pl-12 pr-4 py-4 bg-white/5 border-2 border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-[#06B6D4] focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed" placeholder="Your company" data-id="element-75" />
                </div>
              </div>

              {/* Message field */}
              <div data-id="element-76">
                <label htmlFor="message" className="block text-sm font-semibold text-white mb-2" data-id="element-77">
                  Message *
                </label>
                <div className="relative" data-id="element-78">
                  <MessageSquare className={`absolute left-4 top-4 w-5 h-5 transition-colors ${focusedField === 'message' ? 'text-[#06B6D4]' : 'text-gray-500'}`} data-id="element-79" />
                  <textarea id="message" name="message" required rows={5} value={formData.message} onChange={handleChange} onFocus={() => setFocusedField('message')} onBlur={() => setFocusedField(null)} disabled={isSubmitting} className="w-full pl-12 pr-4 py-4 bg-white/5 border-2 border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-[#06B6D4] focus:outline-none transition-colors resize-none disabled:opacity-50 disabled:cursor-not-allowed" placeholder="Tell us about your growth challenge..." data-id="element-80" />
                </div>
              </div>

              {/* Status message */}
              <AnimatePresence mode="wait" data-id="element-81">
                {submitStatus !== 'idle' && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className={`flex items-start gap-3 p-4 rounded-xl ${submitStatus === 'success' ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`} data-id="element-82">
                    {submitStatus === 'success' ? <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" data-id="element-83" /> : <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-84" />}
                    <p className={`text-sm ${submitStatus === 'success' ? 'text-green-300' : 'text-red-300'}`} data-id="element-85">
                      {statusMessage}
                    </p>
                  </motion.div>}
              </AnimatePresence>

              {/* Submit button */}
              <motion.button type="submit" disabled={isSubmitting} whileHover={!isSubmitting ? {
              scale: 1.02
            } : {}} whileTap={!isSubmitting ? {
              scale: 0.98
            } : {}} className="w-full px-8 py-4 bg-gradient-to-r from-[#0F52BA] to-[#06B6D4] text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-[#0F52BA]/30 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100" data-id="element-86">
                {isSubmitting ? <>
                    <motion.div animate={{
                  rotate: 360
                }} transition={{
                  duration: 1,
                  repeat: Infinity,
                  ease: 'linear'
                }} className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full" data-id="element-87" />
                    <span data-id="element-88">Sending...</span>
                  </> : <>
                    <span data-id="element-89">Send Message</span>
                    <Send className="w-5 h-5" data-id="element-90" />
                  </>}
              </motion.button>
            </form>
          </motion.div>
        </div>

        {/* Bottom bar */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.6
      }} className="mt-24 pt-8 border-t border-white/10" data-id="element-91">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-92">
            <a href="/" onClick={scrollToTop} className="flex items-center gap-3 cursor-pointer group" data-id="element-93">
              <div className="w-8 h-8 relative transition-transform group-hover:scale-110 duration-200" data-id="element-94">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0F52BA] to-[#06B6D4] rounded-full" data-id="element-95"></div>
                <div className="absolute inset-[3px] bg-[#0A1628] rounded-full" data-id="element-96"></div>
                <div className="absolute inset-[6px] bg-gradient-to-br from-[#0F52BA] to-[#06B6D4] rounded-full" data-id="element-97"></div>
              </div>
              <span className="text-xl font-bold text-white group-hover:text-[#06B6D4] transition-colors duration-200" data-id="element-98">
                Think Act
              </span>
            </a>

            <p className="text-sm text-gray-400" data-id="element-99">
              © {new Date().getFullYear()} Think Act. All rights reserved.
            </p>
          </div>
        </motion.div>
      </div>
    </footer>;
}